import React from 'react';
import { Grid, Card, Typography } from 'antd';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  ReferenceArea,
} from 'recharts';
import { formatDate } from '../../utils/dateFormatters';
import { filterByNits } from '../../utils/inventoryReports';

const { useBreakpoint } = Grid;
const { Text } = Typography;

// Threshold constants
const THRESHOLDS = {
  negra: 10,
  roja: 30,
  amarilla: 70,
  verde: 100,
  azul: 200,
};

// Threshold lines config
const THRESHOLD_LINES = [
  { key: 'negra',    name: 'Negra',    value: THRESHOLDS.negra,    stroke: '#4472C4' },
  { key: 'roja',     name: 'Roja',     value: THRESHOLDS.roja,     stroke: '#ed3131' },
  { key: 'amarilla', name: 'Amarilla', value: THRESHOLDS.amarilla, stroke: '#f3eb03' },
  { key: 'verde',    name: 'Verde',    value: THRESHOLDS.verde,    stroke: '#1aed0f' },
  { key: 'azul',     name: 'Azul',     value: THRESHOLDS.azul,     stroke: '#0982f3' },
];

// Colored zones between thresholds
const ZONES = [
  { y1: 0,                    y2: THRESHOLDS.negra,    fill: '#333333', label: 'Negra (<10%)' },
  { y1: THRESHOLDS.negra,     y2: THRESHOLDS.roja,     fill: '#FF0000', label: 'Roja (10-30%)' },
  { y1: THRESHOLDS.roja,      y2: THRESHOLDS.amarilla, fill: '#ca8a04', label: 'Amarilla (30-70%)' },
  { y1: THRESHOLDS.amarilla,  y2: THRESHOLDS.verde,    fill: '#16a34a', label: 'Verde (70-100%)' },
  { y1: THRESHOLDS.verde,     y2: THRESHOLDS.azul,     fill: '#2563eb', label: 'Azul (>100%)' },
];

/**
 * BufferLevelSignalChart - Line chart with threshold zones showing inventory health indicator
 *
 * Displays a "traffic light" chart where:
 * - 5 colored zones represent inventory health levels (ReferenceArea)
 * - 5 constant horizontal lines mark the thresholds between zones
 * - 1 variable line shows the health indicator: % Inventario Promedio = sumatoria(% SKU) / Total SKUs
 *
 * The indicator moves between zones showing real-time inventory buffer status.
 *
 * @param {Object} props
 * @param {Array} props.data - Array of DailyInventoryDistributionRecord
 * @param {string[]} props.nitList - Selected NITs for filtering
 * @param {boolean} props.groupByNit - Whether data is grouped by NIT
 */
function BufferLevelSignalChart({ data, nitList, groupByNit }) {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Transform data: aggregate by date, compute health indicator
  const chartData = React.useMemo(() => {
    if (!data || data.length === 0) return [];

    const relevantData = filterByNits(data, nitList);

    const groupedByDate = {};
    relevantData.forEach(record => {
      const date = record.RecordDate;
      if (!groupedByDate[date]) {
        groupedByDate[date] = {
          RecordDate: date,
          VerdeCount: 0,
          AmarilloCount: 0,
          TotalReferences: 0,
          ProviderCount: 0,
          WeightedPercentSum: 0,
        };
      }
      groupedByDate[date].VerdeCount       += record.VerdeCount || 0;
      groupedByDate[date].AmarilloCount    += record.AmarilloCount || 0;
      groupedByDate[date].TotalReferences  += record.TotalReferences || 0;
      groupedByDate[date].ProviderCount    += 1;
      // Sumatoria ponderada: cada rango aporta (porcentaje * conteo)
      groupedByDate[date].WeightedPercentSum += record.AvgPercentInventory;
        // (record.NegroCount || 0) +
        // (record.RojoCount || 0) +
        // (record.AmarilloCount || 0) +
        // (record.VerdeCount || 0) +
        // (record.AzulCount || 0);
    });

    return Object.values(groupedByDate)
      .sort((a, b) => a.RecordDate.localeCompare(b.RecordDate))
      .map(d => {
        const indicador = d.WeightedPercentSum / d.ProviderCount;
        return {
          RecordDate: d.RecordDate,
          // Constant threshold values for every data point
          negra:    THRESHOLDS.negra,
          roja:     THRESHOLDS.roja,
          amarilla: THRESHOLDS.amarilla,
          verde:    THRESHOLDS.verde,
          azul:     THRESHOLDS.azul,
          // The variable indicator
          indicador,
          // Raw counts for tooltip
          VerdeCount: d.VerdeCount,
          AmarilloCount: d.AmarilloCount,
          TotalReferences: d.TotalReferences,
          ProviderCount: d.ProviderCount,
        };
      });
  }, [data, nitList]);

  // Tick interval: show ~15 labels max
  const tickInterval = React.useMemo(() => {
    if (chartData.length <= 15) return 0;
    return Math.floor(chartData.length / 15);
  }, [chartData.length]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    const entry = chartData.find(d => d.RecordDate === label);
    if (!entry) return null;

    const indicadorVal = entry.indicador;
    let zone = 'Negra';
    let zoneColor = '#333333';
    if (indicadorVal >= THRESHOLDS.verde) { zone = 'Azul'; zoneColor = '#2563eb'; }
    else if (indicadorVal >= THRESHOLDS.amarilla) { zone = 'Verde'; zoneColor = '#16a34a'; }
    else if (indicadorVal >= THRESHOLDS.roja) { zone = 'Amarilla'; zoneColor = '#ca8a04'; }
    else if (indicadorVal >= THRESHOLDS.negra) { zone = 'Roja'; zoneColor = '#dc2626'; }

    return (
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        minWidth: '220px',
      }}>
        <p style={{ margin: '0 0 8px 0', fontWeight: 600, color: '#1f2937' }}>
          {formatDate(label)}
        </p>
        <p style={{ margin: '4px 0', color: zoneColor, fontWeight: 700, fontSize: '13px' }}>
          Indicador: {(indicadorVal).toFixed(1)}% — Zona {zone}
        </p>
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #e5e7eb' }}>
          <p style={{ margin: '2px 0', color: zoneColor, fontSize: '11px' }}>
            Promedio % Inventario: {(indicadorVal).toFixed(1)}% ({entry.TotalReferences} SKUs)
          </p>
        </div>
        <div style={{ marginTop: 6 }}>
          <p style={{ margin: '2px 0', color: '#000000', fontSize: '11px' }}>Umbral Negra: {THRESHOLDS.negra.toFixed(2)}</p>
          <p style={{ margin: '2px 0', color: '#ed3e31', fontSize: '11px' }}>Umbral Roja: {THRESHOLDS.roja.toFixed(2)}</p>
          <p style={{ margin: '2px 0', color: '#f0da10', fontSize: '11px' }}>Umbral Amarilla: {THRESHOLDS.amarilla.toFixed(2)}</p>
          <p style={{ margin: '2px 0', color: '#00ff44', fontSize: '11px' }}>Umbral Verde: {THRESHOLDS.verde.toFixed(2)}</p>
          <p style={{ margin: '2px 0', color: '#5B9BD5', fontSize: '11px' }}>Umbral Azul: {THRESHOLDS.azul.toFixed(2)}</p>
        </div>
      </div>
    );
  };

  if (!chartData || chartData.length === 0) {
    return (
      <Card
        bordered={false}
        className="shadow-sm"
        style={{ height: '100%', borderRadius: '12px', backgroundColor: '#fafafa' }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 300,
          color: '#9ca3af',
          textAlign: 'center',
          padding: 20,
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>{'📊'}</div>
          <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 500, marginBottom: 8 }}>
            Datos insuficientes
          </div>
          <div style={{ fontSize: isMobile ? 12 : 13 }}>
            Se requieren datos de inventario para mostrar el semáforo de amortiguador
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      bordered={false}
      className="shadow-sm"
      style={{
        height: '100%',
        borderRadius: '12px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      }}
      bodyStyle={{ padding: isMobile ? 16 : 20 }}
    >
      {/* Header */}
      <div style={{ marginBottom: isMobile ? 16 : 20 }}>
        <Text strong style={{ fontSize: isMobile ? 14 : 16, color: '#1f2937' }}>
          {'Semáforo de Nivel de Inventario'}
        </Text>
        <br />
        <Text type="secondary" style={{ fontSize: isMobile ? 11 : 12 }}>
          El indicador verde muestra el inventario promedio. Cuando está en zona roja/negra, hay riesgo de desabastecimiento.
        </Text>
      </div>

      {/* Colored zone bands */}
      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 20, bottom: 50 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />

          {/* Reference zones with colored bands */}
          {ZONES.map(({ y1, y2, fill }) => (
            <ReferenceArea key={`${y1}-${y2}`} y1={y1} y2={y2} fill={fill} fillOpacity={0.12} />
          ))}

          <XAxis
            dataKey="RecordDate"
            tickFormatter={formatDate}
            angle={-45}
            textAnchor="end"
            height={80}
            style={{ fontSize: isMobile ? 9 : 11, color: '#6b7280' }}
            tickLine={false}
            interval={tickInterval}
          />
          <YAxis
            domain={[0, 2.0]}
            style={{ fontSize: isMobile ? 9 : 11, color: '#6b7280' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: isMobile ? 10 : 12, paddingTop: 8 }}
            iconType="line"
          />

          {/* Threshold lines (constant horizontal) */}
          {THRESHOLD_LINES.map(({ key, name, value, stroke }) => (
            <Line
              key={key}
              type="linear"
              dataKey={key}
              name={name}
              stroke={stroke}
              strokeWidth={2.5}
              dot={false}
              isAnimationActive={false}
            />
          ))}

          {/* The variable indicator line */}
          <Line
            type="linear"
            dataKey="indicador"
            name="Indicador"
            stroke="#70AD47"
            strokeWidth={2.5}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
}

export default BufferLevelSignalChart;
